import { standardMeasureLabels } from '../../pages/DataExtraction/DataExtractionTemplate/components/ResultsDataPanel/ResultDataFormats';
import { getReviewId } from './Review';
import {
  AdditionalFieldCategory,
  AdditionalField,
  InterventionField,
  DataExtractionTemplate,
  OutcomeTypes,
  DataExtractionTemplateOutcome,
  DataExtractionTemplateResultType,
  TemplateHiddenFields,
  OutcomeDetail,
  ConsolidatedMeasure,
  CustomFieldSupportedSuggestion,
} from 'types/DataExtraction';
import {
  COPY_DATA_EXTRACTION_TEMPLATE_API_ROUTE,
  DATA_EXTRACTION_TEMPLATE_API_ROUTE,
  tokenizeRoute,
} from 'query/routes';
import { getCsrfToken } from 'query';

type DataExtractionTemplateAPI = {
  custom_fields: {
    id?: number;
    name: string;
    category: AdditionalFieldCategory;
  }[];
  custom_field_suggestions?: CustomFieldSupportedSuggestion[];
  interventions: {
    interventions: {
      id?: number;
      name: string;
      position?: number;
    }[];
    characteristics: {
      id?: number;
      name: string;
    }[];
  };
  population: {
    baseline_characteristics: {
      id?: number;
      name: string;
    }[];
  };
  outcomes: {
    outcomes: DataExtractionTemplateOutcome[];
    default_continuous_measure: string;
    default_dichotomous_measure: string;
    data_types: {
      name: string;
      description: string;
      outcome_type: OutcomeTypes;
    }[];
    outcome_details: OutcomeDetail[];
  };
  custom_result_types: DataExtractionTemplateResultType[];
  template_outcome_details: OutcomeDetail[];
  has_extracted_data?: boolean;
  hidden_fields: TemplateHiddenFields;
  updated_by?: {
    first_name: string;
    last_name: string;
    id: string;
  };
  updated_at?: string;
};

type DataExtractionTemplateAPIRequest = {
  revisit_studies: boolean;
} & DataExtractionTemplateAPI;

type DataExtractionTemplateAPIResponse = {
  number_of_revisitable_studies: number;
} & DataExtractionTemplateAPI;

const getDETemplate = async (): Promise<DataExtractionTemplate> => {
  const reviewId = getReviewId();
  if (!reviewId) throw new Error("Couldn't get Review ID.");

  const url = getTemplateUrl(reviewId);

  return fetch(url.toString(), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
    .then((res) => res.json())
    .then((res: DataExtractionTemplateAPIResponse) => {
      const {
        custom_fields = [],
        outcomes = { outcomes: [], data_types: [], outcome_details: [] },
        population = { baseline_characteristics: [] },
        interventions = { interventions: [], characteristics: [] },
        custom_result_types,
        has_extracted_data = false,
        hidden_fields,
        number_of_revisitable_studies,
        template_outcome_details,
        updated_by,
        updated_at,
        custom_field_suggestions,
      } = res;

      const [consolidatedMeasures, labelsModified] = consolidateMeasures(
        custom_result_types
      );

      return {
        identification: {
          additionalFields: filterCustomFields(
            custom_fields,
            AdditionalFieldCategory.IDENTIFICATION
          ),
        },
        methods: {
          additionalFields: filterCustomFields(
            custom_fields,
            AdditionalFieldCategory.METHODS
          ),
        },
        population: {
          ...population,
          additionalFields: filterCustomFields(
            custom_fields,
            AdditionalFieldCategory.POPULATION
          ),
        },
        interventions: {
          interventions: mapInterventions(interventions.interventions),
          characteristics: interventions.characteristics,
        },
        outcomes: {
          outcomes: outcomes.outcomes,
          default_continuous_measure: res.outcomes.default_continuous_measure,
          default_dichotomous_measure: res.outcomes.default_dichotomous_measure,
          data_types: outcomes.data_types,
          outcome_details: outcomes.outcome_details,
        },
        consolidated_measures: consolidatedMeasures,
        consolidated_measures_labels_modified: labelsModified,
        has_extracted_data,
        hidden_fields,
        number_of_revisitable_studies,
        template_outcome_details,
        updated_by,
        updated_at,
        custom_field_suggestions,
      };
    });
};

const getTemplateUrl = (reviewId: string) => {
  const sourceReviewId = new URLSearchParams(document.location.search).get(
    'source_review_id'
  );

  if (sourceReviewId) {
    // Copying template from source review
    return tokenizeRoute(COPY_DATA_EXTRACTION_TEMPLATE_API_ROUTE, {
      review_id: reviewId,
      source_review_id: sourceReviewId,
    });
  } else {
    return tokenizeRoute(DATA_EXTRACTION_TEMPLATE_API_ROUTE, {
      review_id: reviewId,
    });
  }
};

export type UpdateResponseBody = {
  errors?: string[];
};

const updateDETemplate = async (
  {
    identification,
    methods,
    population,
    interventions,
    outcomes,
    consolidated_measures,
    hidden_fields,
    template_outcome_details,
  }: DataExtractionTemplate & {
    hidden_fields: TemplateHiddenFields;
  },
  revisitStudies: boolean
): Promise<{
  status: number;
  body: UpdateResponseBody;
}> => {
  const allAdditionalFields = [
    ...asCustomField(methods.additionalFields, AdditionalFieldCategory.METHODS),
    ...asCustomField(
      identification.additionalFields,
      AdditionalFieldCategory.IDENTIFICATION
    ),
    ...asCustomField(
      population.additionalFields,
      AdditionalFieldCategory.POPULATION
    ),
  ];

  const withOutcomeReorder = outcomes.outcomes.map((outcome) => {
    const timepoints = outcome.timepoints.map((timepoint, index) => {
      return {
        ...timepoint,
        position: index + 1,
      };
    });
    return {
      ...outcome,
      timepoints,
    };
  });

  // Template updates should hardcode standard_deviation / event_count values
  const default_continuous_measure = 'standard_deviation';
  const default_dichotomous_measure = 'event_count';

  const body: DataExtractionTemplateAPIRequest = {
    custom_fields: allAdditionalFields,
    interventions: {
      interventions: mapInterventions(interventions.interventions),
      characteristics: interventions.characteristics,
    },
    population: {
      baseline_characteristics: population.baseline_characteristics,
    },
    outcomes: {
      outcomes: withOutcomeReorder,
      default_continuous_measure,
      default_dichotomous_measure,
      // data types is set to an empty array to satisfy Typescript as we use the same
      // type for reading and writing the API, however the API does not use data type
      // on writes (it is not part of the write API and is ignored)
      data_types: [],
      outcome_details: outcomes.outcome_details,
    },
    custom_result_types: generatedFromConsolidatedMeasures(
      consolidated_measures
    ),
    template_outcome_details,
    hidden_fields,
    revisit_studies: revisitStudies,
  };

  const reviewId = getReviewId();
  if (!reviewId) throw new Error("Couldn't get Review ID.");

  const url = tokenizeRoute(DATA_EXTRACTION_TEMPLATE_API_ROUTE, {
    review_id: reviewId,
  });

  return fetch(url.toString(), {
    method: 'PUT',
    headers: {
      'X-CSRF-Token': getCsrfToken(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  }).then((response) => {
    if (response.status === 200) {
      // All good, carry on
      return { status: response.status, body: {} as UpdateResponseBody };
    }
    return response.json().then((json) => {
      // parse the JSON body so we can do things with it
      return { status: response.status, body: json as UpdateResponseBody };
    });
  });
};

const createEmptyDETemplate = (): DataExtractionTemplate => {
  return {
    identification: {
      additionalFields: [],
    },
    methods: {
      additionalFields: [],
    },
    population: {
      baseline_characteristics: [],
      additionalFields: [],
    },
    interventions: {
      interventions: [],
      characteristics: [],
    },
    outcomes: {
      outcomes: [],
      data_types: [],
      default_continuous_measure: '',
      default_dichotomous_measure: '',
      outcome_details: [],
    },
    hidden_fields: [],
    consolidated_measures: [],
    consolidated_measures_labels_modified: false,
    has_extracted_data: false,
    template_outcome_details: [],
    number_of_revisitable_studies: 0,
  };
};

const filterCustomFields = (
  fields: DataExtractionTemplateAPI['custom_fields'],
  category: AdditionalFieldCategory
): AdditionalField[] => {
  const idFields = fields.filter((field) => field.category === category);
  return idFields.map((field) => {
    return { id: field.id, name: field.name };
  });
};

const mapInterventions = (
  interventions: InterventionField[]
): InterventionField[] => {
  return interventions.map((intervention, index) => {
    return {
      ...intervention,
      position: index + 1,
    };
  });
};

const asCustomField = (
  fields: AdditionalField[],
  category: AdditionalFieldCategory
): DataExtractionTemplateAPI['custom_fields'] => {
  return fields.map((field) => {
    return { id: field.id, name: field.name, category: category };
  });
};

const outcome_types = [
  'ContinuousOutcome',
  'DichotomousOutcome',
  'QualitativeOutcome',
];

/**
 * Converts consolidated measures back to a flat array of custom result types.
 *
 * This function takes the consolidated measure structure and flattens it back to individual
 * custom result types that the API expects. For each consolidated measure, it ensures that
 * all outcome types (ContinuousOutcome, DichotomousOutcome, QualitativeOutcome) are
 * represented, creating new entries if they don't exist.
 *
 * @param consolidated_measures - Array of consolidated measures with grouped custom result types
 * @returns Flat array of individual custom result type objects
 *
 * @example
 * // See specs
 */
const generatedFromConsolidatedMeasures = (
  consolidated_measures: ConsolidatedMeasure[]
): DataExtractionTemplateResultType[] => {
  // Flatten the consolidated measures back to individual custom result types
  const flattened: DataExtractionTemplateResultType[] = [];

  consolidated_measures.forEach((measure) => {
    // Add all custom_result_types from each consolidated measure
    // and update the label to be the consolidated measure's label
    // ensure that for each measure a custom_result_type is created for each outcome type
    outcome_types.forEach((outcomeType) => {
      const existingType = measure.custom_result_types.find(
        (type) => type.outcome_type === outcomeType
      );
      if (existingType) {
        // If the type already exists, use it
        flattened.push({
          ...existingType,
          label: measure.label, // Use the consolidated label
        });
      } else {
        // If the type doesn't exist, create a new one
        flattened.push({
          outcome_type: outcomeType,
          label: measure.label,
        });
      }
    });
  });

  return flattened;
};

const uniqueSuffix = (label: string, labels: string[]) => {
  let index = 1;
  let suffix = ` (custom)`;
  while (labels.includes(label.toLowerCase() + suffix)) {
    suffix = ` (custom ${index})`;
    index++;
  }
  return suffix;
};

const addSuffixForStandardMeasures = (
  label: string,
  labels: string[]
): [string, boolean] => {
  let modified = false;
  if (standardMeasureLabels.includes(label.toLowerCase())) {
    label += uniqueSuffix(label, labels);
    modified = true;
  }
  return [label, modified];
};
/**
 * Consolidates custom result types by grouping them under unique labels.
 *
 * This function takes a flat array of custom result types and groups them by their label,
 * creating a consolidated structure where each unique label has an array of associated
 * custom result types.
 *
 * @param custom_result_types - Array of individual custom result type objects
 * @returns Array of consolidated measures grouped by label
 *
 * @example
 * // See specs
 */

const consolidateMeasures = (
  custom_result_types: DataExtractionTemplateResultType[]
): [ConsolidatedMeasure[], boolean] => {
  // Consolidate measures by outcome type
  const consolidated: ConsolidatedMeasure[] = [];

  // Group custom_result_types by label
  const groupedByLabel = custom_result_types.reduce((acc, item) => {
    if (!acc[item.label.toLowerCase()]) {
      acc[item.label.toLowerCase()] = [];
    }
    acc[item.label.toLowerCase()].push(item);
    return acc;
  }, {} as Record<string, DataExtractionTemplateResultType[]>);

  const allLowercaseLabels = custom_result_types.map((type) =>
    type.label.toLowerCase()
  );

  let labelsModified = false;
  // Create consolidated array with unique labels
  Object.entries(groupedByLabel).forEach(([, types]) => {
    // Determine if any of the types in this group are used
    const used = types.some((type) => type.used);
    const [label, modified] = addSuffixForStandardMeasures(
      types[0].label,
      allLowercaseLabels
    );
    if (modified) {
      labelsModified = true;
    }
    consolidated.push({
      label: label,
      used,
      custom_result_types: types,
    });
  });

  return [consolidated, labelsModified];
};

export {
  getDETemplate,
  updateDETemplate,
  createEmptyDETemplate,
  generatedFromConsolidatedMeasures, // Exported only for spec
  consolidateMeasures, // Exported only for spec
  mapInterventions, // Exported only for spec
};
